import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, withRouter } from 'react-router-dom';
import { UserTypes } from '@guryou/core';
import {
  AppBar,
  Avatar,
  CalendarIcon,
  CentersIcon,
  DefaultText,
  FlexRow,
  generateImageUrl,
  IconButton,
  ImageLogo,
  LibraryIcon,
  ListViewIcon,
  LogOutIcon,
  MenuIcon,
  MoreIcon,
  OverviewIcon,
  ProfileIcon,
  Spinner,
  Toolbar,
} from '@guryou/html-components';
import { sizes } from '@guryou/html-theme';
import { LOGOUT, stateIsLoading, useCheckIsMobile, useCurrentUser } from '@guryou/redux';
import { Menu as MuiMenu, MenuItem as MuiMenuItem } from '@material-ui/core';
import Badge from '@material-ui/core/Badge';
import AssistantIcon from '@material-ui/icons/Assistant';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import PlaylistAddCheckIcon from '@material-ui/icons/PlaylistAddCheck';
import logo from 'assets/img/logo.png';
import { useSidebarAdmin } from './SidebarAdminContext';

const getUnfinishedTasksCount = data => {
  const finishedTasksCount = Object.values(data).filter(value => value === true).length || 0;
  const total = 7;
  return total - finishedTasksCount;
};

const AdminHeader = ({ getCurrentPageTitle }) => {
  const { isOpen, toggleSidebar } = useSidebarAdmin();

  return (
    <header className="bg-white border-b border-gray-200 z-30 h-20">
      <nav className="flex items-center justify-between h-full px-6">
        <div className="flex items-center space-x-4">
          {toggleSidebar && (
            <button
              type="button"
              className="inline-flex items-center justify-center p-2.5 text-gray-500 rounded-lg hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-all duration-200"
              onClick={e => {
                e.currentTarget.blur();
                toggleSidebar();
              }}
              onMouseDown={e => e.preventDefault()}
              title={isOpen ? 'Close menu' : 'Open menu'}
            >
              {isOpen ? (
                <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8.99994 10 7 11.9999l1.99994 2M12 5v14M5 4h14c.5523 0 1 .44772 1 1v14c0 .5523-.4477 1-1 1H5c-.55228 0-1-.4477-1-1V5c0-.55228.44772-1 1-1Z"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m7 10 1.99994 1.9999-1.99994 2M12 5v14M5 4h14c.5523 0 1 .44772 1 1v14c0 .5523-.4477 1-1 1H5c-.55228 0-1-.4477-1-1V5c0-.55228.44772-1 1-1Z"
                  />
                </svg>
              )}
            </button>
          )}

          <div className="h-6 w-px bg-gray-300"></div>

          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{getCurrentPageTitle()}</h1>
          </div>
        </div>

        <div className="flex items-center space-x-4"></div>
      </nav>
    </header>
  );
};

const Header = props => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = event => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const [anchorElMobile, setAnchorElMobile] = useState(null);
  const handleClickMobile = event => setAnchorElMobile(event.currentTarget);
  const handleCloseMobile = () => setAnchorElMobile(null);

  const isMobile = useCheckIsMobile();

  const location = useLocation();

  const isItDeal = location?.pathname.includes('/management/') || location?.pathname.includes('/providers/');
  const isAdmin = location?.pathname.includes('/admin');
  const isAiStudio = location.pathname.includes('/guryou-ai-studio');

  const changeAvatar = useSelector(state => state.user.changeAvatar);
  const providerInfo = useSelector(state => state.provider.generalInfo);
  const providerTasks = useSelector(state => state.providers.completedTasks);
  const pendingCustomers = useSelector(state => state.approvedCustomers.checkPendingCustomers);

  const { user } = useCurrentUser();
  const taskCount = getUnfinishedTasksCount(providerTasks?.data || {});

  const getCurrentPageTitle = () => {
    if (isAdmin) {
      const path = location.pathname.split('/').pop();
      const titles = {
        dashboard: t('GENERAL.dashboard'),
        providers: t('PROVIDERS.title'),
        users: t('GENERAL.users'),
        'deal-translation': 'Deal - Translation',
        'landing-translation': 'Landing - Translation',
      };
      return titles[path] || t('GENERAL.admin');
    }
    return '';
  };

  if (isAiStudio) {
    return null;
  }

  if (isAdmin) {
    return <AdminHeader getCurrentPageTitle={getCurrentPageTitle} />;
  }

  const isLoading = stateIsLoading(providerTasks) && stateIsLoading(pendingCustomers);

  return (
    <AppBar color="inherit" height={sizes.appBarHeight}>
      <Toolbar>
        {user && providerInfo?.data && isMobile && <IconButton icon={MenuIcon} label={t('GENERAL.menu')} onClick={handleClickMobile} />}
        <ImageLogo src={logo} onClick={() => props.history.push('/management/providers')} />
        {user && (
          <FlexRow marginLeft="auto" marginRight={0} width="auto" alignItems="center" justifyContent="flex-end">
            {!isMobile &&
              (!user || stateIsLoading(changeAvatar) ? (
                <Spinner />
              ) : (
                <>
                  {!isLoading && providerInfo?.data ? (
                    <FlexRow xs={5} justifyContent="center">
                      <p className="font-extrabold mr-0 text-base mt-2.5 text-center">({providerInfo?.data?.timezone})</p>
                      <p className="text-xs text-gray-500 text-center">{t('GENERAL.timezone')}</p>
                    </FlexRow>
                  ) : null}
                  {!isLoading && providerInfo?.data ? (
                    <FlexRow xs={2}>
                      <IconButton
                        onClick={() => {
                          props.history.push(`/providers/${providerInfo.data?.id}/provider-progress`);
                        }}
                      >
                        <Badge badgeContent={taskCount} invisible={taskCount === 0} color="secondary">
                          <PlaylistAddCheckIcon />
                        </Badge>
                      </IconButton>
                    </FlexRow>
                  ) : null}
                  {!isLoading && providerInfo?.data ? (
                    <FlexRow xs={2}>
                      <IconButton
                        onClick={() => {
                          props.history.push(`/providers/${providerInfo.data?.id}/pending-flows`);
                        }}
                        tooltip={t('PENDING_FLOWS.title')}
                      >
                        <Badge variant="dot" invisible={!pendingCustomers?.data} color="secondary">
                          <HourglassEmptyIcon />
                        </Badge>
                      </IconButton>
                    </FlexRow>
                  ) : null}
                  <Avatar imageSrc={generateImageUrl(user.image)} imageAlt="user-profile-photo" />
                </>
              ))}

            <IconButton icon={MoreIcon} label={t('GENERAL.menu')} onClick={handleClick} />

            <MuiMenu
              anchorEl={anchorElMobile}
              open={!!anchorElMobile}
              onClose={handleCloseMobile}
              elevation={0}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push(`/providers/${providerInfo.data?.id}/calendar`);
                    handleCloseMobile();
                  }}
                >
                  <CalendarIcon />
                  <DefaultText marginLeft={1}>{t('CALENDAR.titlePlural')}</DefaultText>
                </MuiMenuItem>
              )}
              {isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push(`/providers/${providerInfo.data?.id}/dashboard`);
                    handleCloseMobile();
                  }}
                >
                  <OverviewIcon />
                  <DefaultText marginLeft={1}>{t('GENERAL.dashboard').toUpperCase()}</DefaultText>
                </MuiMenuItem>
              )}
            </MuiMenu>

            <MuiMenu
              anchorEl={anchorEl}
              open={!!anchorEl}
              onClose={handleClose}
              elevation={0}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push('/management/my-profile');
                    handleClose();
                  }}
                >
                  <ProfileIcon />
                  <DefaultText marginLeft={1}>{t('GENERAL.myProfile')}</DefaultText>
                </MuiMenuItem>
              )}
              {isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push('/management/providers');
                    handleClose();
                  }}
                >
                  <CentersIcon />
                  <DefaultText marginLeft={1}>{t('GENERAL.myCenters')}</DefaultText>
                </MuiMenuItem>
              )}
              {isItDeal && user?.role === UserTypes.admin && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push('/management/affiliate-program');
                    handleClose();
                  }}
                >
                  <ListViewIcon />
                  <DefaultText marginLeft={1}>{t('AFFILIATE.affiliate')}</DefaultText>
                </MuiMenuItem>
              )}
              {user?.role === UserTypes.admin && !isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push('/management/providers');
                    handleClose();
                  }}
                >
                  <DefaultText marginLeft={1}>{t('CONFIRMATIONS.goToDeal')}</DefaultText>
                </MuiMenuItem>
              )}
              {user?.role === UserTypes.admin && isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    props.history.push('/admin/dashboard');
                    handleClose();
                  }}
                >
                  <LibraryIcon />
                  <DefaultText marginLeft={1}>{t('CONFIRMATIONS.goToAdmin')}</DefaultText>
                </MuiMenuItem>
              )}

              {user?.role === UserTypes.admin && isItDeal && (
                <MuiMenuItem
                  onClick={() => {
                    handleClose();
                    props.history.push('/guryou-ai-studio');
                  }}
                >
                  <AssistantIcon />
                  <DefaultText marginLeft={1}>{'Ai studio'}</DefaultText>
                </MuiMenuItem>
              )}
              <MuiMenuItem
                onClick={() => {
                  props.history.push('/login');
                  dispatch({ type: LOGOUT });
                  handleClose();
                }}
              >
                <LogOutIcon />
                <DefaultText marginLeft={1}>{t('GENERAL.logout')}</DefaultText>
              </MuiMenuItem>
            </MuiMenu>
          </FlexRow>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default withRouter(Header);
